// ─────────────────────────────────────────────────────────────────────────────
// EnrichCompanyEmailsDialogComponent
// ─────────────────────────────────────────────────────────────────────────────
// Opened after a manual company match when the source email has AI-parsed sender
// address(es). Lists each address with a toggle (default OFF — most parsed emails
// are noise/suppliers), except addresses already in the registry, which are
// pre-toggled ON.
//
// Scope selector (WB-7092): a "Shipping Location" dropdown lets the user target
// either the whole company (default — no location) or a specific location of that
// company. The pre-toggled "already registered" set reflects the CURRENTLY
// selected scope: the company registry (passed in via `data.registeredEmails`)
// when no location is chosen, or that location's own registry (fetched on demand)
// once a location is selected.
//
// Two save modes (see `data.deferSave`):
//   - Immediate (Order details page, default): on Save the toggled-ON addresses
//     are written to the selected scope's registry via the bulk endpoint —
//     company (POST /v1/company-emails/bulk) or location
//     (POST /v1/location-emails/bulk), both idempotent on duplicates — then the
//     dialog closes with `true`.
//   - Deferred (side-by-side correction panel): on confirm the dialog writes
//     nothing and closes returning `{ emails, locationId }`; the caller commits
//     them (to the company or the location) as part of its own save flow.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { SharedModule } from 'src/app/shared/shared.module';
import { SharedDialogComponent } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { CommonService } from 'src/app/shared/common.service';
import { CustomerEmailService } from 'src/app/features/knowledge-base/services/customer-email.service';
import {
  ICreateCustomerEmailsBulkPayload,
  ICreateLocationEmailsBulkPayload,
} from 'src/app/features/knowledge-base/models/customer-email.models';
import { ILocation } from 'src/app/features/customers/models/customers.models';

/** Value returned by the dialog in deferred mode (see `data.deferSave`). */
export interface IEnrichCompanyEmailsDeferredResult {
  emails: string[];
  /** null → commit to the company registry; otherwise the target location id. */
  locationId: number | null;
}

export interface IEnrichCompanyEmailsDialogData {
  /** Matched company id (wl.company) — the registry owner. */
  customerId: number;
  /** Optional company name, shown in the dialog copy. */
  companyName?: string;
  /** AI-parsed email addresses to offer. */
  emails: string[];
  /** Lowercased emails already in the company registry — pre-toggled ON. */
  registeredEmails?: Set<string>;
  /** The matched company's locations, for the Shipping Location dropdown. */
  locations?: ILocation[];
  /**
   * When true, the dialog does NOT write to the registry on confirm; it closes
   * returning an IEnrichCompanyEmailsDeferredResult so the caller commits it as
   * part of its own save flow. Defaults to immediate-write mode.
   */
  deferSave?: boolean;
}

interface IEmailRow {
  email: string;
  enabled: boolean;
}

@Component({
  selector: 'app-enrich-company-emails-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SharedModule,
    SharedDialogComponent,
  ],
  templateUrl: './enrich-company-emails-dialog.component.html',
  styleUrls: ['./enrich-company-emails-dialog.component.scss'],
})
export class EnrichCompanyEmailsDialogComponent implements OnInit, OnDestroy {

  rows: IEmailRow[] = [];
  locations: ILocation[] = [];
  /** null = company-level (default); otherwise the selected location id. */
  selectedLocationId: number | null = null;
  isSaving = false;

  private companyRegistry = new Set<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<EnrichCompanyEmailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IEnrichCompanyEmailsDialogData,
    private customerEmailService: CustomerEmailService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    // De-duplicate (case-insensitive), trim, drop blanks. Pre-toggle ON any email
    // already in the company's registry; the rest default OFF.
    this.companyRegistry = this.data?.registeredEmails ?? new Set<string>();
    this.locations = this.data?.locations ?? [];
    const seen = new Set<string>();
    this.rows = (this.data?.emails ?? [])
      .map((email) => (email ?? '').trim())
      .filter((email) => {
        const key = email.toLowerCase();
        if (!email || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((email) => ({ email, enabled: this.companyRegistry.has(email.toLowerCase()) }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedCount(): number {
    return this.rows.filter((row) => row.enabled).length;
  }

  /** Locations offered in the dropdown — only those with a usable id (a location
   * without an id would bind [ngValue]="undefined" and be misread as company scope). */
  get selectableLocations(): ILocation[] {
    return this.locations.filter((location) => location.id != null);
  }

  /** Re-toggle rows against the newly selected scope's registry (decision 3):
   * the company registry when no location is chosen, otherwise that location's
   * own registry (fetched on demand). */
  onLocationChange(): void {
    if (this.selectedLocationId == null) {
      this.applyRegistry(this.companyRegistry);
      return;
    }
    const locationId = this.selectedLocationId;
    this.customerEmailService.listByLocation(locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (this.selectedLocationId !== locationId) return; // superseded
          const registered = new Set(
            (response?.data ?? [])
              .map((row) => (row.email ?? '').trim().toLowerCase())
              .filter(Boolean),
          );
          this.applyRegistry(registered);
        },
        // On failure, reset toggles against an empty (unknown) location registry
        // rather than leaving the previous scope's pre-toggles — otherwise a Save
        // would write the prior scope's toggled rows to the newly selected
        // location. A same-scope duplicate is idempotent on save.
        error: () => {
          if (this.selectedLocationId !== locationId) return;
          this.applyRegistry(new Set());
        },
      });
  }

  save(): void {
    if (this.isSaving) return;

    const selectedEmails = this.rows.filter((row) => row.enabled).map((row) => row.email);
    if (selectedEmails.length === 0) {
      this.dialogRef.close(false);
      return;
    }

    // Deferred mode: hand the selections + target scope back to the caller, which
    // commits them as part of its own save flow (side-by-side panel).
    if (this.data.deferSave) {
      this.dialogRef.close({ emails: selectedEmails, locationId: this.selectedLocationId });
      return;
    }

    // Immediate mode: write to the selected scope's registry now via the bulk
    // endpoint — one request, idempotent on duplicates.
    this.isSaving = true;
    const request$ = this.selectedLocationId != null
      ? this.customerEmailService.createLocationBulk(
          { location_id: this.selectedLocationId, emails: selectedEmails } as ICreateLocationEmailsBulkPayload)
      : this.customerEmailService.createBulk(
          { customer_id: this.data.customerId, emails: selectedEmails } as ICreateCustomerEmailsBulkPayload);

    request$
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe({
        next: () => {
          this.commonService.showToast(
            this.translateService.instant('quotations_and_orders.enrich_company_emails.success'),
            'SUCCESS',
          );
          this.dialogRef.close(true);
        },
        error: () => {
          // Keep the dialog open so the user can retry.
          this.commonService.showToast(
            this.translateService.instant('quotations_and_orders.enrich_company_emails.error'),
            'ERROR',
          );
        },
      });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  /** Human label for a location option: name, plus city/country when available. */
  locationLabel(location: ILocation): string {
    const parts = [location.name, location.city, location.country?.name]
      .map((part) => (part ?? '').trim())
      .filter(Boolean);
    return parts.join(' - ') || (location.name ?? '');
  }

  /** Reset each row's toggle to whether it's already in the given registry set. */
  private applyRegistry(registered: Set<string>): void {
    this.rows = this.rows.map((row) => ({
      ...row,
      enabled: registered.has(row.email.toLowerCase()),
    }));
  }
}
